import { forwardRef } from 'react';
import { Box, Theme } from '@mui/material';
import { SxProps } from '@mui/system';
import {
    DesignSystemColorModeFlattened,
    DesignSystemIconSizing,
} from '../../theme/design-system/types';
import { RemixiconComponentType } from '@remixicon/react';
import { iconSizingValues } from '../../theme/design-system/tokens';
import { useSlotContext } from '../../utils/useSlotContext';
import { AvailableSlots } from '../../utils/Slots';

export type IconProps = {
    /**
     * The icon component imported from '@remixicon/react'.
     * e.g., import { RiHomeLine } from '@remixicon/react';
     * <RemixIcon icon={RiHomeLine} />
     */
    icon: RemixiconComponentType;
    /**
     * The color of the icon. Can be a design system color only.
     * @default 'ds.fg.neutral.primary'
     */
    color?: DesignSystemColorModeFlattened | 'inherit';
    /**
     * The size of the icon in pixels.
     * @default 24
     */
    size?: DesignSystemIconSizing;
    /**
     * MUI sx prop for custom styling overrides.
     */
    sx?: SxProps<Theme>;
    /**
     * Optional title for accessibility. If provided, aria-hidden will be removed.
     */
    title?: string;
    /**
     * Slot name for use in component composition.
     */
    slot?: Extract<AvailableSlots, 'icon' | 'leadingItem' | 'trailingItem'>;
    /**
     * Class name for additional styling.
     */
    className?: string;
};

const defaultColor: DesignSystemColorModeFlattened = 'ds.fg.neutral.primary';
const defaultSize: DesignSystemIconSizing = 'sm';

/**
 * Renders an icon from the Remix Icon library with design system color and size control.
 */
export const RemixIcon = forwardRef<HTMLSpanElement, IconProps>(
    (props, ref) => {
        const mergedProps = useSlotContext(props, props.slot);

        const {
            icon: IconComponent,
            color = defaultColor,
            size = defaultSize,
            sx,
            title,
            ...rest
        } = mergedProps;

        return (
            <Box
                ref={ref}
                component="span"
                aria-hidden={title ? undefined : true} // Hide decorative icons
                title={title}
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: color,
                    width: iconSizingValues[size],
                    height: iconSizingValues[size],
                    ...sx,
                }}
                {...rest}
            >
                <IconComponent
                    size={iconSizingValues[size]}
                    color="currentColor"
                />
            </Box>
        );
    },
);
